import type {
  AuditEntry,
  MilestoneDraft,
  MilestoneEdit,
} from "./planOfRecordData";
import type {
  CreateMilestonePayload,
  PlanOfRecordMilestone,
  PlanOfRecordMilestoneRow,
  UpdateMilestonePayload,
} from "./planOfRecord.types";

export const PLAN_OF_RECORD_DATASET_TYPE = "plan-of-record-milestones";

/**
 * created_at/last_modified_at are single timestamps, not a log — there is no per-field
 * diff to show, and only one rationale column exists (the most recent write's), so it
 * cannot honestly be attributed to the creation entry once the row has been edited since.
 * Deliberately omits newValue/priorValue: showing newValue alone rendered as
 * "(not set) -> <current date>", implying the date was once unset, which is never true.
 */
function synthesizeMilestoneAudit(row: PlanOfRecordMilestoneRow): AuditEntry[] {
  const created: AuditEntry = {
    id: `${row.id}-created`,
    actor: row.created_by_name,
    at: row.created_at,
    action: "created",
  };

  // created_at and last_modified_at are stamped by separate clock reads within the same
  // save() — they differ by milliseconds on a row nobody has edited since creation, so an
  // exact-equality check would spuriously flag every untouched milestone as edited.
  const genuinelyModified =
    row.last_modified_at &&
    Math.abs(
      new Date(row.last_modified_at).getTime() -
        new Date(row.created_at).getTime(),
    ) > 5000;

  if (!genuinelyModified) {
    return [{ ...created, rationale: row.rationale ?? undefined }];
  }

  const modified: AuditEntry = {
    id: `${row.id}-modified`,
    actor: row.last_modified_by_name,
    at: row.last_modified_at,
    action: "updated",
    rationale: row.rationale ?? undefined,
  };

  return [modified, created];
}

export function mapMilestoneRowToMilestone(
  row: PlanOfRecordMilestoneRow,
): PlanOfRecordMilestone {
  return {
    id: row.id,
    type: row.milestone_type,
    label: row.label,
    scope: row.scope,
    scopeLabel: row.scope_label,
    plannedDate: row.planned_date,
    originalPlannedDate: row.original_planned_date,
    createdAt: row.created_at,
    createdBy: row.created_by_name,
    actualDate: row.actual_date,
    thresholds:
      row.at_risk_within_days == null
        ? null
        : { atRiskWithinDays: row.at_risk_within_days },
    provenance: row.provenance,
    rationale: row.rationale,
    audit: synthesizeMilestoneAudit(row),
    status: row.status,
    driftDays: row.drift,
  };
}

export function buildCreateMilestonePayload(
  draft: MilestoneDraft,
  rationale: string,
): CreateMilestonePayload {
  return {
    milestone_type: draft.type,
    label: draft.label,
    scope: draft.scope,
    scope_label: draft.scopeLabel,
    ...(draft.scope === "country" ? { country_name: draft.countryName } : {}),
    ...(draft.scope === "site" ? { site_number: draft.siteNumber } : {}),
    planned_date: draft.plannedDate,
    actual_date: draft.actualDate,
    at_risk_within_days: draft.thresholds?.atRiskWithinDays ?? null,
    rationale,
  };
}

export function buildUpdateMilestonePayload(
  edit: MilestoneEdit,
  rationale: string,
): UpdateMilestonePayload {
  return {
    planned_date: edit.plannedDate,
    actual_date: edit.actualDate,
    at_risk_within_days: edit.thresholds?.atRiskWithinDays ?? null,
    rationale,
  };
}
